// @ts-nocheck
// ============================================
// Firebase Slice - управление синхронизацией с Firebase
// ============================================

import { StateCreator } from 'zustand';
import { isFirebaseAvailable, subscribeToProjects, subscribeToHistory, subscribeToVersions, subscribeToScreens, subscribeToEvents } from '../../lib/firebaseService';

export interface FirebaseState {
  // Firebase state
  isFirebaseConfigured: boolean;
  isFirebaseSyncing: boolean;
  firebaseUnsubscribe: (() => void) | null;
  historyUnsubscribe: (() => void) | null;
  versionsUnsubscribe: (() => void) | null;
  screensUnsubscribe: (() => void) | null;
  eventsUnsubscribe: (() => void) | null;
  lastLocalUpdate: number; // Timestamp of last local update to prevent race conditions
  
  // Firebase actions
  syncWithFirebase: (userId?: string) => void;
  syncHistoryWithFirebase: (projectId?: string) => void;
  syncVersionsWithFirebase: (projectId?: string) => void;
  syncScreensWithFirebase: (projectId?: string) => void;
  syncEventsWithFirebase: (projectId?: string) => void;
  updateLastLocalUpdate: () => void; // Update timestamp after local changes
  resetLastLocalUpdate: () => void; // Reset to 0 to allow Firebase updates
}

// Global sync lock to prevent concurrent sync calls
// This fixes flickering issue when cold Cloud Functions cause multiple syncs
let _syncLock = false;
let _syncLockTimeout: NodeJS.Timeout | null = null;

const _acquireSyncLock = (): boolean => {
  if (_syncLock) {
    console.log('[Sync] ⚠️ Sync lock active - skipping duplicate call');
    return false;
  }
  _syncLock = true;
  console.log('[Sync] 🔒 Acquired sync lock');
  
  // Auto-release after 3 seconds to prevent permanent lock
  if (_syncLockTimeout) clearTimeout(_syncLockTimeout);
  _syncLockTimeout = setTimeout(() => {
    _syncLock = false;
    _syncLockTimeout = null;
    console.log('[Sync] 🔓 Auto-released sync lock after timeout');
  }, 3000);
  
  return true;
};

const _releaseSyncLock = () => {
  if (_syncLockTimeout) {
    clearTimeout(_syncLockTimeout);
    _syncLockTimeout = null;
  }
  _syncLock = false;
  console.log('[Sync] 🔓 Released sync lock');
};

export const createFirebaseSlice: StateCreator<FirebaseState, [], [], FirebaseState> = (set, get) => ({
  // Initial state
  isFirebaseConfigured: false,
  isFirebaseSyncing: false,
  firebaseUnsubscribe: null,
  historyUnsubscribe: null,
  versionsUnsubscribe: null,
  screensUnsubscribe: null,
  eventsUnsubscribe: null,
  lastLocalUpdate: 0,
  
  // Update timestamp after local changes
  updateLastLocalUpdate: () => {
    set({ lastLocalUpdate: Date.now() });
  },
  
  // Reset lastLocalUpdate to 0 to allow Firebase updates
  // Use this before manual sync when we want to ensure Firebase data is applied
  resetLastLocalUpdate: () => {
    set({ lastLocalUpdate: 0 });
    console.log('[FirebaseSlice] resetLastLocalUpdate called - Firebase updates will be applied');
  },
  
  // Sync with Firebase
  syncWithFirebase: (explicitUserId?: string) => {
    const { isFirebaseSyncing, firebaseUnsubscribe } = get();
    
    console.log('[Sync] syncWithFirebase called, explicitUserId:', explicitUserId);
    
    // Use lock to prevent concurrent sync calls (fixes flickering with cold functions)
    if (!_acquireSyncLock()) {
      console.log('[Sync] ⏭️ Skipping - another sync in progress');
      set({ isFirebaseSyncing: false });
      return;
    }
    
    if (!isFirebaseAvailable()) {
      console.log('[Sync] Firebase not available, skipping sync');
      _releaseSyncLock();
      set({ isFirebaseSyncing: false });
      return;
    }
    
    if (isFirebaseSyncing) {
      console.log('[Sync] Already syncing, skipping');
      return;
    }
    
    if (firebaseUnsubscribe) {
      firebaseUnsubscribe();
    }
    
    // Получаем userId из параметров или из authSlice
    const authUser = (get() as any).user;
    const userId = explicitUserId || authUser?.id;
    
    console.log('[Sync] UserId resolved:', userId, 'explicitUserId:', explicitUserId, 'fromAuthSlice:', authUser?.id);
    
    // CRITICAL FIX: Validate userId exists and is valid before proceeding
    // Empty string, undefined, null, or whitespace-only are not valid
    const validUserId = userId && typeof userId === 'string' && userId.trim() ? userId.trim() : null;
    
    if (!validUserId) {
      console.log('[Sync] ❌ No valid userId available, skipping sync. authUser:', authUser, 'explicitUserId:', explicitUserId);
      set({ isFirebaseSyncing: false });
      _releaseSyncLock();
      return;
    }
    
    set({ isFirebaseSyncing: true, isFirebaseConfigured: true });
    console.log('[Sync] Starting Firebase sync for user:', validUserId, 'DEV mode:', import.meta.env.DEV);
    
    // Store timestamp when sync started to detect race conditions
    const syncStartTime = Date.now();
    
    // CRITICAL FIX: Reset lastLocalUpdate for manual sync to ensure we get latest data
    // This fixes the issue where accepting an invitation doesn't update the apps list
    // because the race condition check was blocking the update
    set({ lastLocalUpdate: 0, isFirebaseSyncing: true });
    console.log('[Sync] Reset lastLocalUpdate and set syncing to allow manual sync');
    
    const unsubscribe = subscribeToProjects((apps) => {
      const now = Date.now();
      
      // DEBUG: Log EVERY callback from Firebase
      console.log('[Sync] 📥 ===== FIREBASE CALLBACK RECEIVED =====');
      console.log('[Sync] 📥 Apps received from Firebase:', apps?.length);
      apps?.forEach((app: any) => {
        console.log('[Sync] 📥 App:', app.id, app.name, 'versions:', app.versions?.length);
        app.versions?.forEach((v: any) => {
          console.log('[Sync] 📥   Version:', v.id, 'screens:', v.screens?.length);
        });
      });
      
      console.log('[Sync] ⏱️ Timing check - syncStartTime:', syncStartTime, 'now:', now, 'timeSinceSyncStart:', now - syncStartTime, 'ms');
      
      // DEBUG: Log all apps to see which ones were loaded
      console.log('[Sync] 📋 All loaded apps:', apps?.map((a: any) => ({ id: a.id, name: a.name, ownerId: a.ownerId, members: Object.keys(a.members || {}) })));
      
      // Race condition check: skip if local updates were made after sync started
      // FIX: Use a more robust check - skip if ANY local updates were made (not just after syncStartTime)
      // This prevents Firebase from overwriting local changes
      const { lastLocalUpdate } = get();
      
      // Skip if there were local updates after sync started (original check)
      const shouldSkip = lastLocalUpdate > syncStartTime;
      
      // ULTRA FIX: Also skip if local was updated VERY recently (within last 5 seconds)
      // This handles the case where local update happened after syncStartTime was captured
      // but before/onSnapshot callback fired
      // NOTE: lastLocalUpdate === 0 means "no local updates" (used for manual sync override)
      const recentLocalUpdate = lastLocalUpdate > 0 && (now - lastLocalUpdate) < 5000;
      
      console.log('[Sync] ⏱️ Race condition check:', {
        lastLocalUpdate,
        syncStartTime,
        diff: lastLocalUpdate - syncStartTime,
        shouldSkip,
        recentLocalUpdate,
        timeSinceLastUpdate: now - lastLocalUpdate
      });
      
      // CRITICAL FIX: Always merge apps list, even if there are local screen changes
      // This ensures invited projects appear in the list immediately
      // The screen-level differences will be handled separately by the real-time subscriptions
      
      // Check for NEW apps that don't exist locally - these should ALWAYS be added
      const currentApps = get().apps;
      const currentAppIds = new Set(currentApps.map((a: any) => a.id));
      const newApps = apps.filter((a: any) => !currentAppIds.has(a.id));
      
      if (newApps.length > 0) {
        console.log('[Sync] ✅ Found new apps from Firebase:', newApps.map((a: any) => a.name));
        // Always add new apps - this is critical for invitation acceptance
      }
      
      // Check if any current app has more screens than Firebase version
      // This handles the case where local update was made but Firebase hasn't caught up yet
      let shouldSkipScreenUpdate = false;
      if (get().apps.length > 0) {
        for (const currentApp of currentApps) {
          if (currentApp.id === (get() as any).currentApp?.id) {
            // Only check the CURRENT app's screens, not all apps
            const firebaseApp = apps.find((a: any) => a.id === currentApp.id);
            if (firebaseApp && currentApp.versions && firebaseApp.versions) {
              for (const currentVersion of currentApp.versions) {
                if (currentVersion.id === (get() as any).currentVersion?.id) {
                  // Only check CURRENT version
                  const firebaseVersion = firebaseApp.versions.find((v: any) => v.id === currentVersion.id);
                  if (firebaseVersion) {
                    const currentScreenCount = currentVersion.screens?.length || 0;
                    const firebaseScreenCount = firebaseVersion.screens?.length || 0;
                    
                    // If local has more screens, skip screen update for current app only
                    if (currentScreenCount > firebaseScreenCount) {
                      console.log('[Sync] ⚠️ Skipping screen update - local has more screens than Firebase');
                      console.log('[Sync] ⚠️ Local screens:', currentScreenCount, 'Firebase screens:', firebaseScreenCount);
                      shouldSkipScreenUpdate = true;
                    }
                  }
                }
              }
            }
          }
        }
      }
      
      // Only skip if there's a timing race condition, NOT due to screen differences
      // Screen differences are handled separately and shouldn't block app list updates
      if (shouldSkip || recentLocalUpdate) {
        console.log('[Sync] ⚠️⚠️⚠️ SKIPPING FIREBASE UPDATE - LOCAL CHANGES DETECTED', 
          shouldSkip ? '(syncStartTime check)' : '(recent update check)', 
          { shouldSkip, recentLocalUpdate, lastLocalUpdate, syncStartTime, timeSinceLastUpdate: now - lastLocalUpdate });
        set({ isFirebaseSyncing: false });
        return;
      }
      
      console.log('[Sync] ✅✅✅ APPLYING FIREBASE UPDATE - no local changes detected');
      
      if (apps && apps.length > 0) {
        const currentApps = get().apps;
        const currentAppIds = new Set(currentApps.map(a => a.id));
        const newAppIds = new Set(apps.map(a => a.id));
        
        // Check for differences
        const addedApps = apps.filter(a => !currentAppIds.has(a.id));
        const removedApps = currentApps.filter(a => !newAppIds.has(a.id));
        
        if (addedApps.length > 0) {
          console.log('[Sync] ➕ New apps from Firebase:', addedApps.map(a => a.name));
        }
        if (removedApps.length > 0) {
          console.log('[Sync] ➖ Apps removed from Firebase:', removedApps.map(a => a.name));
        }
        
        // Only update if no local changes were made during sync
        set({ apps, isFirebaseSyncing: false });
        console.log('[Sync] ✅✅✅ State updated with Firebase data');
      console.log('[Sync] ✅ New state apps count:', apps.length);
      console.log('[Sync] ✅ First app versions:', apps[0]?.versions?.map((v: any) => ({ id: v.id, screens: v.screens?.length })));
        
        const { currentApp, setCurrentApp, setCurrentVersion, setCurrentScreen } = get() as any;
        
        // Always try to restore from localStorage when apps are loaded
        // This ensures we restore the correct app even if one was selected earlier
        const savedAppId = localStorage.getItem('currentAppId');
        const savedVersionId = localStorage.getItem('currentVersionId');
        const savedScreenId = localStorage.getItem('currentScreenId');
        
        console.log('[Sync] 🔍 Restore check - savedAppId:', savedAppId);
        console.log('[Sync] 🔍 Restore check - savedVersionId:', savedVersionId);
        console.log('[Sync] 🔍 Restore check - savedScreenId:', savedScreenId);
        console.log('[Sync] 🔍 Restore check - current currentApp:', currentApp?.id);
        console.log('[Sync] 🔍 Restore check - received apps:', apps.map((a: any) => ({ id: a.id, name: a.name })));
        
        // FIX: Check if saved app exists in loaded apps
        // This is the key fix - we now check ALL apps (including shared) for the savedAppId
        const appToRestore = savedAppId ? apps.find((a: any) => a.id === savedAppId) : null;
        console.log('[Sync] 🔍 Restore check - appToRestore found:', !!appToRestore, appToRestore?.name);
        
        if (appToRestore) {
          // Restore the saved app from localStorage
          console.log('[Sync] ✅ Restoring app from localStorage:', savedAppId);
          const versions = appToRestore.versions || [];
          const activeVersion = savedVersionId 
            ? versions.find((v: any) => v.id === savedVersionId)
            : versions.find((v: any) => v.isActive) || versions[0];
          
          const screenToRestore = savedScreenId && activeVersion?.screens?.find((s: any) => s.id === savedScreenId);
          
          set({ 
            currentApp: appToRestore, 
            currentAppId: appToRestore.id,
            currentVersion: activeVersion || null,
            currentVersionId: activeVersion?.id || null,
            currentScreen: screenToRestore || activeVersion?.screens?.[0] || null,
            currentScreenId: screenToRestore?.id || activeVersion?.screens?.[0]?.id || null
          });
          
          // Sync history for the restored app
          get().syncHistoryWithFirebase(appToRestore.id);
          // Start real-time sync for versions, screens, events
          get().syncVersionsWithFirebase(appToRestore.id);
          get().syncScreensWithFirebase(appToRestore.id);
          get().syncEventsWithFirebase(appToRestore.id);
        } else if (!savedAppId && (!currentApp || (currentApp && !apps.find((a: any) => a.id === currentApp.id)))) {
          // Only set default app if:
          // 1. Nothing is saved in localStorage AND
          // 2. Nothing is selected (currentApp is null) OR current app is no longer in the apps list
          console.log('[Sync] ⚠️ No saved app or current app not in list, selecting first available app');
          
          // Find first app that user is a member of (not just demo)
          const userId = (get() as any).user?.id;
          
          // FIX: Priority order for selecting app:
          // 1. First check for any non-pending member apps (includes accepted invitations)
          // 2. Then check for pending invitations
          // 3. Finally fall back to first available app
          const activeMemberApp = userId ? apps.find((a: any) => 
            a.members?.[userId] && 
            a.members[userId].status === 'active'
          ) : null;
          
          const pendingMemberApp = userId ? apps.find((a: any) => 
            a.members?.[userId] && 
            a.members[userId].status === 'pending'
          ) : null;
          
          // Prefer active members over pending
          const memberApp = activeMemberApp || pendingMemberApp;
          const appToSelect = memberApp || apps[0];
          
          console.log('[Sync] 📌 Selected app:', appToSelect?.name, 'isMemberApp:', !!memberApp, 'isActiveMember:', !!activeMemberApp);
          setCurrentApp(appToSelect.id);
          localStorage.setItem('currentAppId', appToSelect.id);
          get().syncHistoryWithFirebase(appToSelect.id);
          // Also sync versions, screens, events for real-time updates
          get().syncVersionsWithFirebase(appToSelect.id);
          get().syncScreensWithFirebase(appToSelect.id);
          get().syncEventsWithFirebase(appToSelect.id);
        } else if (currentApp) {
          // Current app still exists in list, but check if we should switch to a better app
          // This handles the case where user accepts an invitation while on demo app
          // Note: userId already defined above in this callback scope
          
          // Check if current app is a demo app and there's a better option available
          const isCurrentAppDemo = currentApp.isDemo;
          
          // CRITICAL FIX: Also check if current app is owned by user (not just demo)
          // If user has both demo and invited projects, prefer invited (non-owned) projects
          const isCurrentAppOwnedByUser = currentApp.ownerId === userId;
          
          // Find ANY non-owned member app (includes accepted invitations)
          // This has higher priority than demo app switch
          if (userId && !isCurrentAppOwnedByUser && currentApp.members?.[userId]) {
            // Current app is a shared project - check if we should stay or switch
            const currentMemberStatus = currentApp.members[userId]?.status;
            
            // If current is active member, keep it - no need to switch
            if (currentMemberStatus === 'active') {
              console.log('[Sync] ✅ Current app is active member app, keeping:', currentApp.id);
            }
          }
          
          if (isCurrentAppDemo && userId) {
            // Find a non-demo member app (including accepted invitations)
            // PRIORITY: First check for active members, then pending
            const nonDemoActiveMemberApp = apps.find((a: any) => 
              !a.isDemo && 
              a.members?.[userId] && 
              a.members[userId].status === 'active'
            );
            
            const nonDemoPendingMemberApp = apps.find((a: any) => 
              !a.isDemo && 
              a.members?.[userId] && 
              a.members[userId].status === 'pending'
            );
            
            // Prefer active over pending
            const nonDemoMemberApp = nonDemoActiveMemberApp || nonDemoPendingMemberApp;
            
            if (nonDemoMemberApp) {
              console.log('[Sync] 🔄 Switching from demo app to member app:', nonDemoMemberApp.name, 'status:', nonDemoMemberApp.members[userId]?.status);
              setCurrentApp(nonDemoMemberApp.id);
              localStorage.setItem('currentAppId', nonDemoMemberApp.id);
              get().syncHistoryWithFirebase(nonDemoMemberApp.id);
              get().syncVersionsWithFirebase(nonDemoMemberApp.id);
              get().syncScreensWithFirebase(nonDemoMemberApp.id);
              get().syncEventsWithFirebase(nonDemoMemberApp.id);
              return;
            }
          }
          
          // Default: just sync current app
          console.log('[Sync] ✅ Current app still valid:', currentApp.id);
          
          // Note: userId is already defined in this scope from line 256
          if (userId) {
            // Find first active or pending member app (excluding current)
            const memberApp = apps.find((a: any) => 
              a.members?.[userId] && 
              a.members[userId].status === 'active' &&
              a.id !== currentApp.id
            );
            
            if (memberApp) {
              console.log('[Sync] 🔄 Found active member app (invitation accepted), switching:', memberApp.name);
              setCurrentApp(memberApp.id);
              localStorage.setItem('currentAppId', memberApp.id);
              get().syncHistoryWithFirebase(memberApp.id);
              get().syncVersionsWithFirebase(memberApp.id);
              get().syncScreensWithFirebase(memberApp.id);
              get().syncEventsWithFirebase(memberApp.id);
              return;
            }
          }
          
          get().syncHistoryWithFirebase(currentApp.id);
          // Also sync versions, screens, events for real-time updates
          get().syncVersionsWithFirebase(currentApp.id);
          get().syncScreensWithFirebase(currentApp.id);
          get().syncEventsWithFirebase(currentApp.id);
        }
      } else {
        set({ isFirebaseSyncing: false });
      }
    }, validUserId); // Передаем validUserId для фильтрации
    
    set({ firebaseUnsubscribe: unsubscribe });
    
    // Release lock after setting up subscription (subscription runs asynchronously)
    _releaseSyncLock();
  },
  
  // Sync history with Firebase
  syncHistoryWithFirebase: (projectId?: string) => {
    const { historyUnsubscribe } = get();
    
    if (!isFirebaseAvailable()) {
      console.log('[History Sync] Firebase not available, skipping history sync');
      return;
    }
    
    // Unsubscribe from previous history subscription
    if (historyUnsubscribe) {
      historyUnsubscribe();
    }
    
    // Get current project id if not provided
    const currentProjectId = projectId || (get() as any).currentApp?.id;
    
    if (!currentProjectId) {
      console.log('[History Sync] No projectId available, skipping history sync');
      return;
    }
    
    console.log('[History Sync] Starting history sync for project:', currentProjectId);
    
    // Получаем userId
    const userId = (get() as any).user?.id;
    
    if (!userId) {
      console.log('[History Sync] No userId available, skipping history sync');
      return;
    }
    
    const unsubscribe = subscribeToHistory(currentProjectId, userId, (history) => {
      console.log('[History Sync] Received history from Firebase:', history?.length);
      set({ history: history || [] });
    });
    
    set({ historyUnsubscribe: unsubscribe });
  },

  // Sync versions with Firebase - real-time updates for versions
  // FIX: Versions and screens are stored in the main app document, not subcollections
  // The subcollections may be empty - we use data from the main document
  syncVersionsWithFirebase: (projectId?: string) => {
    const { versionsUnsubscribe, apps } = get();
    
    // Clean up old subscriptions
    if (versionsUnsubscribe) {
      versionsUnsubscribe();
    }
    
    // Get current project id if not provided
    const currentProjectId = projectId || (get() as any).currentApp?.id;
    
    if (!currentProjectId) {
      console.log('[Versions Sync] No projectId available, skipping versions sync');
      return;
    }
    
    // Get the app from the current apps list - versions are stored in the main document
    const currentApps = get().apps;
    const currentAppData = currentApps.find((app: any) => app.id === currentProjectId);
    
    if (!currentAppData) {
      console.log('[Versions Sync] App not found in apps list, skipping:', currentProjectId);
      return;
    }
    
    // Versions are already loaded in the app from the main subscription
    // Just update currentVersion if needed
    console.log('[Versions Sync] Using versions from main document:', currentAppData.versions?.length || 0);
    
    // Get saved version ID from localStorage
    const savedVersionId = localStorage.getItem('currentVersionId');
    const currentVersion = (get() as any).currentVersion;
    const versions = currentAppData.versions || [];
    
    // Find the version - prefer saved one, then active, then first
    let matchedVersion = null;
    if (savedVersionId) {
      matchedVersion = versions.find((v: any) => v.id === savedVersionId);
    }
    if (!matchedVersion) {
      matchedVersion = versions.find((v: any) => v.isActive);
    }
    if (!matchedVersion) {
      matchedVersion = versions[0];
    }
    
    // Update state with current version and screen
    if (matchedVersion) {
      const screens = matchedVersion.screens || [];
      const savedScreenId = localStorage.getItem('currentScreenId');
      
      // Find the screen - prefer saved one, then first
      let matchedScreen = null;
      if (savedScreenId) {
        matchedScreen = screens.find((s: any) => s.id === savedScreenId);
      }
      if (!matchedScreen) {
        matchedScreen = screens[0];
      }
      
      console.log('[Versions Sync] Setting current version:', matchedVersion.id, 'screens:', screens.length);
      console.log('[Versions Sync] Screen data from matchedVersion:', JSON.stringify(screens.slice(0, 2).map((s: any) => ({ id: s.id, name: s.name, imageUrl: s.imageUrl }))));
      console.log('[Versions Sync] Setting current screen:', matchedScreen?.id, 'name:', matchedScreen?.name, 'imageUrl:', matchedScreen?.imageUrl);
      
      set({ 
        currentVersion: matchedVersion,
        currentVersionId: matchedVersion.id,
        currentScreen: matchedScreen || null,
        currentScreenId: matchedScreen?.id || null
      });
    }
    
    console.log('[Versions Sync] ✅ Versions synced from main document');
    
    // Also trigger screens and events sync
    get().syncScreensWithFirebase(currentProjectId);
  },

  // Sync screens with Firebase - real-time updates for screens
  // FIX: Screens are stored in the main app document (inside versions), not subcollections
  // The subcollections may be empty - we use data from the main document
  syncScreensWithFirebase: (projectId?: string) => {
    const { screensUnsubscribe, apps } = get();
    
    // Unsubscribe from previous subscription
    if (screensUnsubscribe) {
      screensUnsubscribe();
    }
    
    // Get current project id if not provided
    const currentProjectId = projectId || (get() as any).currentApp?.id;
    
    if (!currentProjectId) {
      console.log('[Screens Sync] No projectId available, skipping screens sync');
      return;
    }
    
    // Get the app from the current apps list - screens are stored in versions in the main document
    const currentApps = get().apps;
    const currentAppData = currentApps.find((app: any) => app.id === currentProjectId);
    
    if (!currentAppData) {
      console.log('[Screens Sync] App not found in apps list, skipping:', currentProjectId);
      return;
    }
    
    // Get current version
    const currentVersion = (get() as any).currentVersion;
    const versions = currentAppData.versions || [];
    const currentVersionData = currentVersion 
      ? versions.find((v: any) => v.id === currentVersion.id)
      : versions.find((v: any) => v.isActive) || versions[0];
    
    if (!currentVersionData) {
      console.log('[Screens Sync] No version found, skipping screens sync');
      return;
    }
    
    const screens = currentVersionData.screens || [];
    console.log('[Screens Sync] Using screens from main document:', screens.length);
    
    // Get saved screen ID from localStorage
    const savedScreenId = localStorage.getItem('currentScreenId');
    const currentScreen = (get() as any).currentScreen;
    
    // Find the screen - prefer saved one, then current, then first
    let matchedScreen = null;
    if (savedScreenId) {
      matchedScreen = screens.find((s: any) => s.id === savedScreenId);
    }
    if (!matchedScreen && currentScreen) {
      matchedScreen = screens.find((s: any) => s.id === currentScreen.id);
    }
    if (!matchedScreen) {
      matchedScreen = screens[0];
    }
    
    // Update state with screen
    if (matchedScreen) {
      set({ 
        currentScreen: matchedScreen,
        currentScreenId: matchedScreen.id
      });
      console.log('[Screens Sync] Set current screen:', matchedScreen.id, 'name:', matchedScreen.name);
    }
    
    console.log('[Screens Sync] ✅ Screens synced from main document');
  },

  // Sync events with Firebase - real-time updates for events
  // FIX: Events are stored in the main app document (inside screens in versions), not subcollections
  // The subcollections may be empty - we use data from the main document
  syncEventsWithFirebase: (projectId?: string) => {
    const { eventsUnsubscribe, apps } = get();
    
    // Unsubscribe from previous subscription
    if (eventsUnsubscribe) {
      eventsUnsubscribe();
    }
    
    // Get current project id if not provided
    const currentProjectId = projectId || (get() as any).currentApp?.id;
    
    if (!currentProjectId) {
      console.log('[Events Sync] No projectId available, skipping events sync');
      return;
    }
    
    // Get the app from the current apps list - events are stored in screens in the main document
    const currentApps = get().apps;
    const currentAppData = currentApps.find((app: any) => app.id === currentProjectId);
    
    if (!currentAppData) {
      console.log('[Events Sync] App not found in apps list, skipping:', currentProjectId);
      return;
    }
    
    // Get current version and screen
    const currentVersion = (get() as any).currentVersion;
    const currentScreen = (get() as any).currentScreen;
    const versions = currentAppData.versions || [];
    const currentVersionData = currentVersion 
      ? versions.find((v: any) => v.id === currentVersion.id)
      : versions.find((v: any) => v.isActive) || versions[0];
    
    if (!currentVersionData) {
      console.log('[Events Sync] No version found, skipping events sync');
      return;
    }
    
    const screens = currentVersionData.screens || [];
    const currentScreenData = currentScreen
      ? screens.find((s: any) => s.id === currentScreen.id)
      : screens[0];
    
    const events = currentScreenData?.events || [];
    console.log('[Events Sync] Using events from main document:', events.length);
    
    console.log('[Events Sync] ✅ Events synced from main document');
  }
});
